/**
 * Bounds on the conversation history the panel replays to Sofia each turn.
 *
 * Two independent limits, because they fail differently. The message count
 * keeps ordinary threads short. The BYTE budget is the one that matters on a
 * structured task: a single Sofia reply can run 25-30k characters, so a
 * count-only cap ships hundreds of KB against a 240,000-byte proxy limit —
 * and Sofia trims the assembled prompt to PRODUCT_MAX_INPUT_CHARS (24k) on
 * arrival regardless, so everything above the budget was never going to be
 * read. Sending it only buys payload size and failure risk.
 */

/** Maximum number of prior messages replayed, newest-first. */
export const HISTORY_LIMIT = 24

/**
 * Maximum serialized UTF-8 bytes of replayed history content. The proxy
 * rejects bodies over 240,000 bytes and Sofia's own cap is 256 KiB, so the
 * history's share sits far below both — leaving headroom for the current
 * message, the view-context envelope, and JSON escaping overhead. Measured in
 * bytes, not string length: `.length` counts UTF-16 code units and can
 * undercount multibyte text several-fold against a byte-denominated ingress
 * limit.
 */
export const HISTORY_BYTE_BUDGET = 80_000

/** Per-message allowance for its JSON envelope ("role", "content", quoting). */
const MESSAGE_OVERHEAD_BYTES = 40

const encoder = new TextEncoder()

interface HistoryMessage {
  role: string
  content: string
}

/** UTF-8 size of a message's content plus its JSON envelope allowance. */
function messageBytes(message: HistoryMessage): number {
  return encoder.encode(message.content).length + MESSAGE_OVERHEAD_BYTES
}

/**
 * Cut content to at most `maxBytes` of UTF-8, keeping the TAIL — the same end
 * the responder's own `text[-24000:]` safety clamp keeps, so what the client
 * retains matches what the server would keep anyway. Decoding with
 * `fatal: false` turns a code point split at the cut into U+FFFD, which is
 * then trimmed.
 */
function tailBytes(content: string, maxBytes: number): string {
  const bytes = encoder.encode(content)
  if (bytes.length <= maxBytes) {
    return content
  }
  return new TextDecoder("utf-8", { fatal: false })
    .decode(bytes.slice(bytes.length - maxBytes))
    .replace(/^�+/, "")
}

/**
 * Trim history to the newest messages that fit both limits.
 *
 * Drops OLDEST-first and never splits a message it can drop instead — a
 * half-message replayed as a whole turn is worse than an absent one. The one
 * exception is a newest message that alone exceeds the whole budget: dropping
 * it would silently change what the user is responding to, and keeping it
 * whole ships a request the ingress rejects outright, so it is truncated to
 * the budget instead.
 */
export function boundHistory<T extends HistoryMessage>(history: T[]): T[] {
  const recent = history.slice(-HISTORY_LIMIT)
  if (recent.length === 0) {
    return recent
  }
  const kept: T[] = []
  let used = 0
  for (let index = recent.length - 1; index >= 0; index -= 1) {
    const message = recent[index]
    const cost = messageBytes(message)
    if (kept.length === 0 && cost > HISTORY_BYTE_BUDGET) {
      kept.push({
        ...message,
        content: tailBytes(
          message.content,
          HISTORY_BYTE_BUDGET - MESSAGE_OVERHEAD_BYTES,
        ),
      })
      break
    }
    if (used + cost > HISTORY_BYTE_BUDGET) {
      break
    }
    kept.push(message)
    used += cost
  }
  return kept.reverse()
}
